import { useEffect, useState } from "react";
import { getCases } from "../lib/api";
import { motion } from "framer-motion";

export default function CaseStudies() {
  const [cases, setCases] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => { getCases().then(setCases); }, []);

  const planets = ["All", ...Array.from(new Set(cases.map((c) => c.planet)))];
  const filtered = filter === "All" ? cases : cases.filter((c) => c.planet === filter);

  return (
    <div data-testid="page-cases">
      <section className="pt-16 pb-8">
        <div className="container-page">
          <div className="eyebrow mb-4">Educational Illustrations</div>
          <h1 className="text-5xl md:text-6xl leading-tight max-w-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Case Study Scenarios
          </h1>
          <p className="mt-6 text-lg text-[#475569] max-w-2xl">
            Illustrative teaching cases that demonstrate how planetary indications inform remedy selection within the Astrohomeopathy® framework.
          </p>
          <div className="mt-6 inline-flex items-start gap-3 rounded-2xl bg-[#FEF9E7] border border-[#D4AF37]/30 px-5 py-4 max-w-2xl">
            <div className="text-[#D4AF37] font-semibold text-sm">Note</div>
            <div className="text-sm text-[#475569]">
              These are educational scenarios prepared for teaching purposes — not records of specific patients. Individual outcomes vary. Consult Dr. Manish Misra for personalized assessment.
            </div>
          </div>
        </div>
      </section>

      <section className="pb-8">
        <div className="container-page flex flex-wrap gap-2">
          {planets.map((p) => (
            <button
              key={p}
              onClick={() => setFilter(p)}
              data-testid={`case-filter-${p.toLowerCase()}`}
              className={`px-5 py-2 rounded-full text-sm font-medium transition ${
                filter === p ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-[#0F172A] hover:border-[#D4AF37]"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </section>

      <section className="pb-24">
        <div className="container-page grid md:grid-cols-2 gap-8">
          {filtered.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-3xl overflow-hidden bg-white border border-slate-100 hover:shadow-xl transition-shadow"
              data-testid={`case-${c.id}`}
            >
              <div className="aspect-[16/9] overflow-hidden bg-gradient-to-br from-[#F0F9FF] via-white to-[#FEF9E7] flex items-center justify-center">
                <div className="text-8xl text-[#D4AF37]/60">☉</div>
              </div>
              <div className="p-8">
                <div className="flex flex-wrap gap-3 text-xs mb-4">
                  <span className="px-3 py-1 rounded-full bg-[#F0F9FF] text-[#0EA5E9] tracking-wider uppercase">{c.planet}</span>
                  <span className="px-3 py-1 rounded-full bg-[#FEF9E7] text-[#D4AF37] tracking-wider uppercase">{c.duration}</span>
                </div>
                <h3 className="text-2xl md:text-3xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{c.title}</h3>
                <p className="text-[#475569] leading-relaxed">{c.summary}</p>
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="text-xs tracking-widest uppercase text-[#D4AF37]">Framework</div>
                  <div className="text-[#0F172A] font-medium mt-1">{c.outcome}</div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
